(() => {
    'use strict';

    interface ConsentCategories {
        [key: string]: boolean;
    }

    interface CategoryConfig {
        required: boolean;
        name: string;
    }

    interface AnalyticalScriptConfig {
        src: string;
        global: string;
        init?: () => void;
    }

    interface ConsentConfig {
        cookieDomains: string[];
        metrikaId: string | null;
        gtmId: string;
        storageKey: string;
        consentDuration: {
            accept: number;
            decline: number;
        };
        categories: Record<string, CategoryConfig>;
        analyticalCookies: string[];
        analyticalStoragePatterns: RegExp[];
        analyticalStorageKeys: string[];
        analyticalScripts: AnalyticalScriptConfig[];
    }

    interface ConsentData {
        timestamp: number;
        duration: number;
        categories: ConsentCategories;
    }

    interface HTMLElementWithScrollData extends HTMLElement {
        _scrollY?: number;
    }

    interface OriginalStyles {
        paddingRight?: string;
        overflow?: string;
    }

    interface HTMLHtmlElementWithStyles extends HTMLHtmlElement {
        _originalStyles?: OriginalStyles;
    }

    const CONFIG: ConsentConfig = {
        cookieDomains: [
            '',
            window.location.hostname,
            '.' + window.location.hostname,
            '.ftc.ru',
            'ftc.ru',
            '.digitalcertc.ru',
            '.yandex.ru',
            '.google.ru',
            'mc.yandex.ru'
        ],
        metrikaId: null,
        gtmId: 'GTM-5XHHX36',
        storageKey: 'digitalcertcCookiesConsent',
        consentDuration: {
            accept: 12 * 30 * 24 * 60 * 60,
            decline: 1 * 30 * 24 * 60 * 60
        },
        categories: {
            default: { required: true, name: 'Обязательные' },
            analytical: { required: false, name: 'Аналитические' }
        },
        analyticalCookies: ['_ym_uid', '_ga', '_gid', '_gat', '_ym_isad'],
        analyticalStoragePatterns: [
            /^_ym/,
            /^ym:session:/,
            /^ym:experiments:/,
            /^ym:clickmap:/,
            /^ym:advertising:/,
            /^_ga(?:_|$)/,
            /^_gid$/,
            /^_gat$/,
            /^_gac_/,
            /^AMP_TOKEN$/,
            /^__amp_/,
            /^FPAU/,
            /^_fbp$/,
            /^_fbc$/,
            /^_ttp$/,
            /^_pin_unauth$/,
            /^li_[a-z]/,
            /^gtm_debug$/,
        ],
        analyticalStorageKeys: [
            '_ym_storage', '_ym_isad', '_ym_vh', '_ym_cs', '_ym_s', '_ym_l', '_ym_uid',
            '_ga', '_gid', '_gat',
            '__utma', '__utmb', '__utmc', '__utmz', '__utmt',
            '_fbp', '_fbc'
        ],
        analyticalScripts: [
            {
                src: 'https://www.googletagmanager.com/gtm.js',
                global: '',
                init: initGTM
            },
            /*{ src: 'https://mc.yandex.ru/metrika/tag.js', global: 'yandex_metrika_callbacks2', init: initYandexMetrika },*/
        ]
    };

    let currentSettings: ConsentCategories = {
        default: true,
        analytical: true
    };

    let consentData: ConsentData | null = null;

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init(): void {
        initConsentManager();
        setupEventListeners();
        setupExternalLink();
    }

    function getScrollbarWidth(): number {
        const scrollDiv = document.createElement('div');
        scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;';
        document.body.appendChild(scrollDiv);
        const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
        document.body.removeChild(scrollDiv);
        return scrollbarWidth;
    }

    function lockScroll(): void {
        const html = document.documentElement as HTMLHtmlElementWithStyles;
        const body = document.body as HTMLElementWithScrollData;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const scrollbarWidth = getScrollbarWidth();

        html._originalStyles = html._originalStyles || {
            paddingRight: html.style.paddingRight,
            overflow: html.style.overflow
        };

        if (scrollbarWidth > 0) {
            const currentPadding = parseInt(window.getComputedStyle(html).paddingRight) || 0;
            body.style.paddingRight = (currentPadding + scrollbarWidth) + 'px';
        }

        html.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.width = '100%';
        body.style.top = `-${scrollY}px`;

        body._scrollY = scrollY;
    }

    function unlockScroll(): void {
        const html = document.documentElement as HTMLHtmlElementWithStyles;
        const body = document.body as HTMLElementWithScrollData;
        const originalStyles: OriginalStyles = html._originalStyles || {};
        const scrollY = body._scrollY || 0;

        body.style.paddingRight = originalStyles.paddingRight || '';
        html.style.overflow = originalStyles.overflow || '';

        // Reflow
        void html.offsetWidth;

        body.style.position = '';
        body.style.top = '';
        body.style.width = '';

        if (scrollY > 0) {
            window.scrollTo(0, scrollY);
        }

        delete html._originalStyles;
        delete body._scrollY;
    }

    function initConsentManager(): void {
        consentData = loadConsent();

        if (shouldShowBanner()) {
            showBanner();
            cleanupAnalyticalCookies();
            cleanupAnalyticalLocalStorage();
        } else {
            applyConsent(consentData);
        }
    }

    function shouldShowBanner(): boolean {
        if (!consentData) {
            return true;
        }

        const now = Math.floor(Date.now() / 1000);
        const expiry = consentData.timestamp + consentData.duration;

        if (now > expiry) {
            clearConsent();
            return true;
        }
        return false;
    }

    function loadConsent(): ConsentData | null {
        try {
            const data = localStorage.getItem(CONFIG.storageKey);
            if (!data) {
                return null;
            }

            const parsed: ConsentData = JSON.parse(data);

            if (parsed.categories) {
                for (const key in parsed.categories) {
                    if (Object.prototype.hasOwnProperty.call(parsed.categories, key)) {
                        currentSettings[key] = parsed.categories[key];
                    }
                }
            }

            return parsed;
        } catch (e) {
            console.warn('localStorage not available', e);
            return null;
        }
    }

    function saveConsent(consent: ConsentCategories, duration: number): void {
        try {
            const data: ConsentData = {
                timestamp: Math.floor(Date.now() / 1000),
                duration: duration,
                categories: consent
            };
            localStorage.setItem(CONFIG.storageKey, JSON.stringify(data));
            consentData = data;
        } catch (e) {
            console.warn('Failed to save consent', e);
        }
    }

    function clearConsent(): void {
        try {
            localStorage.removeItem(CONFIG.storageKey);
        } catch (e) {
            console.warn('Failed to clear consent', e);
        }
        consentData = null;
    }

    /**
     * Проверяет, соответствует ли ключ аналитическому паттерну
     */
    function matchesAnalyticalStoragePattern(key: string): boolean {
        for (let i = 0; i < CONFIG.analyticalStoragePatterns.length; i++) {
            if (CONFIG.analyticalStoragePatterns[i].test(key)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Очищает localStorage от аналитических данных
     */
    function cleanupAnalyticalLocalStorage(): void {
        try {
            // Сначала собираем ВСЕ ключи, которые нужно удалить
            const keysToRemove: string[] = [];

            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (!key) {
                    continue;
                }

                // 1. Проверка по точным названиям
                for (let j = 0; j < CONFIG.analyticalStorageKeys.length; j++) {
                    if (key === CONFIG.analyticalStorageKeys[j]) {
                        keysToRemove.push(key);
                        break;
                    }
                }

                // 2. Проверка по паттернам (если ещё не добавлен)
                if (keysToRemove.indexOf(key) === -1 && matchesAnalyticalStoragePattern(key)) {
                    keysToRemove.push(key);
                }
            }

            for (let i = 0; i < keysToRemove.length; i++) {
                localStorage.removeItem(keysToRemove[i]);
            }
        } catch (e) {
            console.warn('Failed to cleanup localStorage', e);
        }
    }

    // ==================== COOKIE MANAGEMENT ====================

    /**
     * Удаляет куки
     */
    function deleteCookie(name: string): void {
        const paths = ['/']; // если куки установлены на разных путях

        const expiresPart = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        const endParts = ['', '; SameSite=Lax', '; SameSite=None; Secure'];

        // Перебираем все комбинации domain + path
        for (let d = 0; d < CONFIG.cookieDomains.length; d++) {
            for (let p = 0; p < paths.length; p++) {
                for (let o = 0; o < endParts.length; o++) {
                    const domainPart = CONFIG.cookieDomains[d] ? `; domain=${CONFIG.cookieDomains[d]}` : '';
                    const pathPart = `; path=${paths[p]}`;

                    document.cookie = `${name}=${domainPart}${pathPart}; ${expiresPart}${endParts[o]}`;
                }
            }
        }
    }

    /**
     * Очищает аналитические куки
     */
    function cleanupAnalyticalCookies(): void {
        // Удаляем аналитические куки
        for (let i = 0; i < CONFIG.analyticalCookies.length; i++) {
            deleteCookie(CONFIG.analyticalCookies[i]);
        }

        // Удаляем все куки, начинающиеся с "_"
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            const eqIdx = cookie.indexOf('=');
            if (eqIdx > 0) {
                const name = cookie.substring(0, eqIdx);
                if (name.charAt(0) === '_') {
                    deleteCookie(name);
                }
            }
        }
    }

    function loadAnalyticalScripts(): void {
        let scriptEl: HTMLScriptElement | null = null;
        const hasScriptInHead = document.head.getElementsByTagName('script').length > 0;

        if (!hasScriptInHead) {
            scriptEl = document.createElement('script');
            document.head.appendChild(scriptEl);
        }

        for (let i = 0; i < CONFIG.analyticalScripts.length; i++) {
            ((scriptConfig: AnalyticalScriptConfig) => {
                // Проверяем, не загружен ли уже скрипт
                const scriptSrcBase = scriptConfig.src.split('?')[0].trim();
                const isScriptLoaded = !!window[scriptConfig.global] || document.querySelector(`script[src*="${scriptSrcBase}"]`);

                if (isScriptLoaded) {
                    if (typeof scriptConfig.init === 'function') {
                        scriptConfig.init();
                    }
                    return;
                }

                if (typeof scriptConfig.init === 'function') {
                    scriptConfig.init();
                }

                if (scriptEl) {
                    scriptEl.remove();
                }
            })(CONFIG.analyticalScripts[i]);
        }
    }

    function initYandexMetrika(): void {
        if (!CONFIG.metrikaId || window[`yaCounter${CONFIG.metrikaId}`]) {
            return;
        }

        ((d: Document, w: Window, c: string) => {
            w[c] = w[c] || [];
            (w[c] as unknown[]).push(() => {
                try {
                    w[`yaCounter${CONFIG.metrikaId}`] = new (window as any).Ya.Metrika2({
                        id: CONFIG.metrikaId,
                        clickmap: true,
                        trackLinks: true,
                        accurateTrackBounce: true,
                        webvisor: true
                    });
                } catch (e) { }
            });
            const n = d.getElementsByTagName("script")[0];
            const s = d.createElement("script");
            const f = () => { n.parentNode.insertBefore(s, n); };
            s.type = "text/javascript";
            s.async = true;
            s.src = "https://mc.yandex.ru/metrika/tag.js";
            if ((w as any).opera === "[object Opera]") {
                d.addEventListener("DOMContentLoaded", f, false);
            } else { f(); }
        })(document, window, "yandex_metrika_callbacks2");
    }

    function initGTM(): void {
        const dataLayer = window['dataLayer'];
        if (dataLayer && dataLayer.find((elem: Record<string, unknown>) => elem['gtm.start'])) {
            return;
        }

        ((w: Window, d: Document, s: string, l: string) => {
            w[l] = w[l] || [];
            w[l].push({
                'gtm.start': new Date().getTime(),
                event: 'gtm.js'
            });
            const f = d.getElementsByTagName(s)[0];
            const j = d.createElement(s) as HTMLScriptElement;
            const dl = l !== 'dataLayer' ? `&l=${l}` : '';
            j.async = true;
            j.src = `https://www.googletagmanager.com/gtm.js?id=${CONFIG.gtmId}${dl}`;
            f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer');
    }

    function applyConsent(consent: ConsentData | null): void {
        if (!consent || !consent.categories) {
            return;
        }

        const hasAnalytical = consent.categories.analytical === true;

        if (hasAnalytical) {
            loadAnalyticalScripts();
        } else {
            cleanupAnalyticalCookies();
            cleanupAnalyticalLocalStorage();
        }
    }

    function showBanner(): void {
        const banner = document.getElementById('cookiesBanner');
        if (banner) {
            banner.classList.add('visible');
            banner.style.display = 'block';
        }
        currentSettings = { default: true, analytical: false };
        updateSettingsCheckboxes();
    }

    function hideBanner(): void {
        const banner = document.getElementById('cookiesBanner');
        if (banner) {
            banner.classList.remove('visible');
            setTimeout(() => {
                banner.style.display = 'none';
            }, 300);
        }
    }

    function setupEventListeners(): void {
        const acceptBtn = document.getElementById('cookiesAcceptBtn');
        const declineBtn = document.getElementById('cookiesDeclineBtn');
        const openSettingsLink = document.getElementById('openSettingsLink');

        if (acceptBtn) {
            acceptBtn.addEventListener('click', handleAcceptAll);
        }
        if (declineBtn) {
            declineBtn.addEventListener('click', handleDeclineAll);
        }
        if (openSettingsLink) {
            const openSettings = () => {
                showSettingsView();
            };
            openSettingsLink.addEventListener('click', openSettings);
        }

        const closeSettingsBtn = document.getElementById('closeSettingsBtn');
        const confirmBtn = document.getElementById('cookiesConfirmBtn');

        if (closeSettingsBtn) {
            closeSettingsBtn.addEventListener('click', () => {
                hideSettingsView();
            });
        }
        if (confirmBtn) {
            confirmBtn.addEventListener('click', handleConfirmSettings);
        }

        const checkboxes = document.querySelectorAll<HTMLInputElement>('.cookies-modal__checkbox:not([disabled])');
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', (e: Event) => {
                const target = e.target as HTMLInputElement;
                toggleCategory(target.dataset.category || '', target.checked);
            });

            checkbox.addEventListener('click', (e: Event) => {
                e.stopPropagation();
            });
        });
    }

    function setupExternalLink(): void {
        const links = document.querySelectorAll<HTMLAnchorElement>(
            '.cookie-settings-link, a[href*="cookie-settings"], a[data-cookie-settings]'
        );
        links.forEach(link => {
            link.addEventListener('click', (e: Event) => {
                e.preventDefault();
                openSettingsFromLink();
            });
        });
    }

    function handleAcceptAll(): void {
        const consent: ConsentCategories = { default: true, analytical: true };
        saveConsent(consent, CONFIG.consentDuration.accept);
        hideBanner();
        window.location.reload();
    }

    function handleDeclineAll(): void {
        const consent: ConsentCategories = { default: true, analytical: false };
        saveConsent(consent, CONFIG.consentDuration.decline);
        cleanupAnalyticalCookies();
        cleanupAnalyticalLocalStorage();
        hideBanner();
        window.location.reload();
    }

    function handleConfirmSettings(): void {
        const consent: ConsentCategories = {};
        for (const key in currentSettings) {
            if (Object.prototype.hasOwnProperty.call(currentSettings, key)) {
                consent[key] = currentSettings[key];
            }
        }

        const hasAnalytical = consent.analytical === true;
        const duration = hasAnalytical ? CONFIG.consentDuration.accept : CONFIG.consentDuration.decline;

        saveConsent(consent, duration);

        if (!hasAnalytical) {
            cleanupAnalyticalCookies();
            cleanupAnalyticalLocalStorage();
        }

        hideBanner();
        window.location.reload();
    }

    function showSettingsView(): void {
        const backdrop = document.getElementById('cookiesModalBackdrop');
        const modal = document.getElementById('cookiesModal');

        if (backdrop && modal) {
            backdrop.hidden = false;
            // Небольшая задержка для анимации
            setTimeout(() => {
                backdrop.classList.add('visible');
            }, 10);
        }

        lockScroll();
        updateSettingsCheckboxes();
    }

    function hideSettingsView(): void {
        const backdrop = document.getElementById('cookiesModalBackdrop');
        const modal = document.getElementById('cookiesModal');

        if (backdrop && modal) {
            backdrop.classList.remove('visible');
            setTimeout(() => {
                backdrop.hidden = true;
            }, 200);
        }

        unlockScroll();
    }

    function openSettingsFromLink(): void {
        const saved = loadConsent();
        if (saved && saved.categories) {
            for (const key in saved.categories) {
                if (Object.prototype.hasOwnProperty.call(saved.categories, key)) {
                    currentSettings[key] = saved.categories[key];
                }
            }
        }

        showSettingsView();
    }

    function toggleCategory(category: string, enabled: boolean): void {
        if (CONFIG.categories[category] && !CONFIG.categories[category].required) {
            currentSettings[category] = enabled;
        }
    }

    function updateSettingsCheckboxes(): void {
        const checkboxes = document.querySelectorAll<HTMLInputElement>('.cookies-modal__checkbox');
        checkboxes.forEach(checkbox => {
            const category = checkbox.dataset.category;
            if (category && Object.prototype.hasOwnProperty.call(currentSettings, category)) {
                checkbox.checked = currentSettings[category];
            }
        });
    }
})();
